<!-- EngineerConsigneeDashboard.vue -->
<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import {
  EyeIcon,
  GlobeIcon,
  RefreshIcon,
  SigmaIcon,
  ClockIcon,
  BuildingIcon,
  MobileIcon,
  EnvelopeIcon,
  CalendarIcon,
} from '@/icons'
import SelectOptionPrime from '@/components/forms/SelectOption/SelectOptionPrime.vue'
import api from '@/services/api'
import StatsCards from '@/components/cards/StatsCards.vue'
import { tenderReportService } from '@/services/tenderReportService'
import { useAuthStore } from '@/stores/auth'
import { AxiosError } from 'axios'

const router = useRouter()
const authStore = useAuthStore()

// Status filter dropdown options
const statusOptions = [
  { label: 'All Consignees', value: '' },
  { label: 'Pending Installation', value: 'pending' },
  { label: 'Partially Installed', value: 'partial' },
  { label: 'Installed', value: 'installed' },
  { label: 'Completed', value: 'completed' },
]

// Filters
const searchFilter = ref('')
const statusFilter = ref('')
const loading = ref(false)
const error = ref<string | null>(null)

// Interface for consignee data with tender info
interface ConsigneeReportCard {
  id: number
  title: string
  itemName: string
  quantity: number
  visibleFields: Record<string, boolean>
  itemGroups: Array<{
    fieldValues: Record<string, string>
  }>
}

interface ConsigneeWithTender {
  id: number
  name: string
  designation: string
  number: string
  emailId: string
  faxNumber: string
  callAlignedDate: string
  assignedEngineerId: string
  assignedEngineerName: string
  installationStatus: string
  totalItems: number
  installedItems: number
  tenderId: string
  tender: {
    id: string
    t247Id: string
    referenceNo: string
    organization: string
    location: string
    winningAmount: number
    contractEndDate: string
  }
  reportCards: ConsigneeReportCard[]
}

interface TenderDetails {
  id: string
  t247Id: string
  referenceNo: string
  organization: string
  location: string
  winningAmount?: number
  estimatedCost: number
  contractEndDate?: string
}

interface ReportConsignee {
  id?: number
  name: string
  designation: string
  number: string
  emailId?: string
  faxNumber?: string
  callAlignedDate?: string
  assignedEngineerId?: string
  assignedEngineerName?: string
  reportCards?: Array<{
    title?: string
    itemName?: string
    quantity?: number
    visibleFields?: Record<string, boolean>
    itemGroups?: Array<{
      fieldValues: Record<string, string>
    }>
  }>
}

interface ApiReport {
  id?: string
  tenderId: string
  consignees?: ReportConsignee[]
  [key: string]: unknown
}

const consignees = ref<ConsigneeWithTender[]>([])
const currentEngineerId = computed(() => authStore.user?.id)

// Filtered consignees based on search and status
const filteredConsignees = computed(() => {
  let filtered = consignees.value

  if (statusFilter.value) {
    filtered = filtered.filter((c) => {
      if (statusFilter.value === 'installed') return c.installationStatus === 'installed'
      if (statusFilter.value === 'pending') return c.installationStatus === 'pending'
      if (statusFilter.value === 'partial') return c.installationStatus === 'partial'
      if (statusFilter.value === 'completed') return c.installationStatus === 'completed'
      return true
    })
  }

  if (searchFilter.value) {
    const searchTerm = searchFilter.value.toLowerCase()
    filtered = filtered.filter(
      (c) =>
        c.name.toLowerCase().includes(searchTerm) ||
        c.tender.referenceNo.toLowerCase().includes(searchTerm) ||
        c.tender.organization.toLowerCase().includes(searchTerm) ||
        c.tender.location.toLowerCase().includes(searchTerm) ||
        c.designation.toLowerCase().includes(searchTerm),
    )
  }

  return filtered
})

// Statistics
const totalConsignees = computed(() => consignees.value.length)
const pendingCount = computed(
  () => consignees.value.filter((c) => c.installationStatus === 'pending').length,
)
const partialCount = computed(
  () => consignees.value.filter((c) => c.installationStatus === 'partial').length,
)
const installedCount = computed(
  () => consignees.value.filter((c) => c.installationStatus === 'installed').length,
)
const totalItemsCount = computed(() =>
  consignees.value.reduce((sum, c) => sum + c.totalItems, 0),
)
const totalInstalledItems = computed(() =>
  consignees.value.reduce((sum, c) => sum + c.installedItems, 0),
)

const installationProgress = computed(() => {
  if (totalItemsCount.value === 0) return 0
  return Math.round((totalInstalledItems.value / totalItemsCount.value) * 100)
})

const fetchAssignedConsignees = async () => {
  try {
    loading.value = true
    error.value = null
    console.log('🔄 Fetching assigned consignees for engineer:', currentEngineerId.value)

    const reports = await tenderReportService.getAllReports() as ApiReport[]
    console.log('📊 All reports:', reports)

    const assignedConsignees: ConsigneeWithTender[] = []
    let consigneeCounter = 0

    for (const report of reports) {
      if (!report.consignees || report.consignees.length === 0) continue

      let tenderDetails: TenderDetails | null = null
      try {
        const tenderResponse = await api.get(`/api/tenders/id/${report.tenderId}`)
        tenderDetails = tenderResponse.data as TenderDetails
      } catch {
        console.warn('Failed to fetch tender details for:', report.tenderId)
        continue
      }

      if (!tenderDetails) continue

      const engineerConsignees = report.consignees.filter(
        (consignee) => consignee.assignedEngineerId === currentEngineerId.value,
      )

      for (const consignee of engineerConsignees) {
        let totalItems = 0
        let installedItems = 0

        for (const card of consignee.reportCards || []) {
          for (const group of card.itemGroups || []) {
            totalItems++
            if (group.fieldValues.installationStatus === 'yes') {
              installedItems++
            }
          }
        }

        let installationStatus = 'pending'
        if (totalItems > 0) {
          if (installedItems === totalItems) {
            installationStatus = 'installed'
          } else if (installedItems > 0) {
            installationStatus = 'partial'
          } else {
            installationStatus = 'pending'
          }
        }

        const reportCards: ConsigneeReportCard[] = (consignee.reportCards || []).map((card, idx: number) => ({
          id: idx + 1,
          title: card.title || '',
          itemName: card.itemName || '',
          quantity: card.quantity || 1,
          visibleFields: card.visibleFields || {},
          itemGroups: card.itemGroups || []
        }))

        consigneeCounter++

        assignedConsignees.push({
          id: consigneeCounter,
          name: consignee.name || '',
          designation: consignee.designation || '',
          number: consignee.number || '',
          emailId: consignee.emailId || '',
          faxNumber: consignee.faxNumber || '',
          callAlignedDate: consignee.callAlignedDate || '',
          assignedEngineerId: consignee.assignedEngineerId || '',
          assignedEngineerName: consignee.assignedEngineerName || '',
          installationStatus,
          totalItems,
          installedItems,
          tenderId: report.tenderId,
          tender: {
            id: tenderDetails.id,
            t247Id: tenderDetails.t247Id,
            referenceNo: tenderDetails.referenceNo,
            organization: tenderDetails.organization,
            location: tenderDetails.location,
            winningAmount: tenderDetails.winningAmount || tenderDetails.estimatedCost || 0,
            contractEndDate: tenderDetails.contractEndDate || '',
          },
          reportCards,
        })
      }
    }

    consignees.value = assignedConsignees
    console.log(`✅ Found ${assignedConsignees.length} consignees assigned to you`)
  } catch (err: unknown) {
    console.error('❌ Error fetching assigned consignees:', err)
    if (err instanceof AxiosError) {
      error.value = err.message
    } else if (err instanceof Error) {
      error.value = err.message
    } else {
      error.value = 'Failed to fetch assigned consignees'
    }
  } finally {
    loading.value = false
  }
}

const getStatusBadgeSeverity = (status: string): string => {
  const severityMap: Record<string, string> = {
    pending: 'warning',
    partial: 'info',
    installed: 'success',
    completed: 'secondary',
  }
  return severityMap[status] || 'secondary'
}

const getStatusLabel = (status: string): string => {
  const labelMap: Record<string, string> = {
    pending: 'Pending',
    partial: 'Partial',
    installed: 'Installed',
    completed: 'Completed',
  }
  return labelMap[status] || status
}

const formatNumber = (num: number): string => {
  if (!num) return '0'
  if (num >= 10000000) return (num / 10000000).toFixed(2) + ' Cr'
  if (num >= 100000) return (num / 100000).toFixed(2) + ' L'
  if (num >= 1000) return (num / 1000).toFixed(2) + ' K'
  return num.toLocaleString('en-IN')
}

const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

// ── Navigate to tender page, passing consigneeName so TenderEngineerView
//    can pin to just this consignee's report ─────────────────────────────────
const viewConsigneeDetails = (consignee: ConsigneeWithTender) => {
  router.push({
    path: `/tenders/${consignee.tender.t247Id}`,
    query: { consigneeName: consignee.name }
  })
}

const clearFilters = () => {
  statusFilter.value = ''
  searchFilter.value = ''
}

const refreshData = () => {
  fetchAssignedConsignees()
}

onMounted(() => {
  fetchAssignedConsignees()
})
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="My Assigned Installations" />

    <!-- Stats Cards -->
    <div class="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
      <StatsCards title="Total Consignees" :value="totalConsignees" :icon="BuildingIcon" />
      <StatsCards title="Pending" :value="pendingCount" :icon="ClockIcon" />
      <StatsCards title="Partial" :value="partialCount" :icon="ClockIcon" />
      <StatsCards title="Installed" :value="installedCount" :icon="SigmaIcon" />
      <StatsCards title="Total Items" :value="totalItemsCount" :icon="SigmaIcon" />
    </div>

    <!-- Progress Card -->
    <CardComponent
      title="Installation Progress"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
      className="mb-6"
    >
      <template #body>
        <div class="space-y-2">
          <div class="flex justify-between text-sm">
            <span class="text-gray-600">Overall Progress</span>
            <span class="font-medium">{{ installationProgress }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2.5">
            <div
              class="bg-green-600 h-2.5 rounded-full transition-all duration-300"
              :style="{ width: `${installationProgress}%` }"
            ></div>
          </div>
          <div class="flex justify-between text-xs text-gray-500">
            <span>{{ totalInstalledItems }} installed</span>
            <span>{{ totalItemsCount - totalInstalledItems }} remaining</span>
          </div>
        </div>
      </template>
    </CardComponent>

    <!-- Error Message -->
    <div v-if="error" class="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-red-800">{{ error }}</p>
          <button @click="fetchAssignedConsignees" class="mt-1 text-sm text-red-700 hover:text-red-600 underline">
            Click here to retry
          </button>
        </div>
      </div>
    </div>

    <!-- Consignee Table -->
    <CardComponent
      className="max-w-full"
      footerClass="justify-end"
      title="Assigned Consignees"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
    >
      <template #header>
        <div class="flex items-center justify-between gap-4">
          <SelectOptionPrime
            v-model="statusFilter"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Filter by Status"
            id="consignee-status-filter"
            size="sm"
            class="w-48"
            :filter="true"
            :editable="false"
          />
          <TextInputDefault
            v-model="searchFilter"
            placeholder="Search by consignee, tender, location..."
            id="searchconsignees"
            name="searchconsignees"
            type="text"
            size="sm"
          />
          <div class="flex gap-2 ml-auto">
            <BaseButton v-if="statusFilter || searchFilter" text="Clear Filters" size="xs" variant="ghost" @click="clearFilters" />
            <BaseButton size="sm" :icon="RefreshIcon" @click="refreshData" :loading="loading" />
          </div>
        </div>
      </template>

      <template #body>
        <div v-if="loading" class="flex flex-1 gap-2 justify-center text-center py-8">
          <i class="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent"></i>
          Loading assigned consignees...
        </div>

        <div v-else-if="consignees.length === 0 && !error" class="w-full flex flex-col items-center text-center py-8 gap-6">
          <GlobeIcon class="w-12 h-12 text-gray-400" />
          <div class="space-y-3">
            <p class="text-lg font-medium text-gray-700">No consignees assigned to you</p>
            <p class="text-sm text-gray-500">You haven't been assigned to any consignee for installation yet</p>
            <p class="text-xs text-gray-400">Consignees will appear here once admin assigns them to you</p>
          </div>
        </div>

        <div v-else-if="filteredConsignees.length === 0 && consignees.length > 0" class="w-full flex flex-col items-center text-center py-8 gap-6">
          <GlobeIcon class="w-12 h-12 text-gray-400" />
          <div class="space-y-3">
            <p class="text-lg font-medium text-gray-700">No matching consignees</p>
            <p class="text-sm text-gray-500">No consignees match your filter criteria. Try adjusting your filters.</p>
            <BaseButton text="Clear Filters" size="sm" variant="outline" @click="clearFilters" />
          </div>
        </div>

        <DataTable
          v-else-if="filteredConsignees.length > 0"
          :value="filteredConsignees"
          class="w-full"
          sortMode="multiple"
          scrollable
          scrollHeight="450px"
          resizableColumns
          columnResizeMode="fit"
          showGridlines
          :pt="{
            table: { style: 'border-collapse: separate; border-spacing: 0;' },
            thead: { style: 'position: sticky; top: 0; z-index: 20;' },
            tbody: { style: 'position: relative; z-index: 10;' },
          }"
        >
          <Column frozen sortable field="name" header="Consignee"
            headerStyle="position: sticky; left: 0; z-index: 30;"
            bodyStyle="position: sticky; left: 0; z-index: 25;"
          >
            <template #body="{ data }">
              <div class="flex flex-col">
                <span class="font-medium">{{ data.name }}</span>
                <span class="text-xs text-gray-500">{{ data.designation }}</span>
              </div>
            </template>
          </Column>

          <Column sortable field="tender.referenceNo" header="Tender Reference">
            <template #body="{ data }">
              <div class="flex flex-col">
                <span class="text-sm">{{ data.tender.referenceNo }}</span>
                <span class="text-xs text-gray-500">{{ data.tender.organization }}</span>
              </div>
            </template>
          </Column>

          <Column sortable field="tender.location" header="Location">
            <template #body="{ data }">
              <div class="text-sm">{{ data.tender.location }}</div>
            </template>
          </Column>

          <Column field="contact" header="Contact">
            <template #body="{ data }">
              <div class="flex flex-col space-y-1 text-xs">
                <div class="flex items-center gap-1">
                  <MobileIcon class="w-3 h-3 text-gray-500" />
                  <span>{{ data.number || 'N/A' }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <EnvelopeIcon class="w-3 h-3 text-gray-500" />
                  <span class="truncate max-w-[150px]">{{ data.emailId || 'N/A' }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <CalendarIcon class="w-3 h-3 text-gray-500" />
                  <span>{{ formatDate(data.callAlignedDate) }}</span>
                </div>
              </div>
            </template>
          </Column>

          <Column field="progress" header="Progress">
            <template #body="{ data }">
              <div class="space-y-1">
                <div class="flex justify-between text-xs">
                  <span>{{ data.installedItems }}/{{ data.totalItems }}</span>
                  <span>{{ Math.round((data.installedItems / data.totalItems) * 100) || 0 }}%</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    class="bg-green-600 h-1.5 rounded-full transition-all duration-300"
                    :style="{ width: `${Math.round((data.installedItems / data.totalItems) * 100) || 0}%` }"
                  ></div>
                </div>
              </div>
            </template>
          </Column>

          <Column sortable field="installationStatus" header="Status">
            <template #body="{ data }">
              <Badge :value="getStatusLabel(data.installationStatus)" :severity="getStatusBadgeSeverity(data.installationStatus)" />
            </template>
          </Column>

          <Column sortable field="tender.winningAmount" header="Contract Value">
            <template #body="{ data }">
              <div class="text-sm font-medium">₹{{ formatNumber(data.tender.winningAmount) }}</div>
            </template>
          </Column>

          <Column sortable field="tender.contractEndDate" header="Completion Deadline">
            <template #body="{ data }">
              <div class="flex flex-col">
                <span class="text-sm">{{ formatDate(data.tender.contractEndDate) }}</span>
                <span
                  v-if="data.tender.contractEndDate"
                  class="text-xs"
                  :class="{
                    'text-red-600': new Date(data.tender.contractEndDate) < new Date(),
                    'text-orange-600': new Date(data.tender.contractEndDate) > new Date(),
                  }"
                >
                  {{ new Date(data.tender.contractEndDate) < new Date() ? 'Overdue' : 'In Progress' }}
                </span>
              </div>
            </template>
          </Column>

          <Column frozen field="action" header="Actions"
            headerStyle="position: sticky; right: 0; z-index: 30;"
            bodyStyle="position: sticky; right: 0; z-index: 25;"
          >
            <template #body="{ data }">
              <BaseButton
                size="xs"
                @click="viewConsigneeDetails(data)"
                variant="secondary"
                :icon="EyeIcon"
                tooltip="View Installation Details"
              />
            </template>
          </Column>
        </DataTable>
      </template>

      <template #footer v-if="!loading && !error && filteredConsignees.length > 0">
        <div class="flex justify-between items-center w-full">
          <div class="text-xs text-gray-500">
            Showing {{ filteredConsignees.length }} of {{ consignees.length }} consignees
            <span v-if="statusFilter">(filtered by: {{ getStatusLabel(statusFilter) }})</span>
          </div>
          <div class="text-xs text-gray-500">
            Total Items: {{ totalItemsCount }} | Installed: {{ totalInstalledItems }} | Last updated: {{ new Date().toLocaleTimeString() }}
          </div>
        </div>
      </template>
    </CardComponent>
  </AdminLayout>
</template>
